import { can } from '~/authorization'
import type { UserRole } from '~/prisma/enums'

export default () => {
  const { data } = useAuth()
  const roles = computed((): UserRole[] => data.value?.user?.roles ?? ['noRole'])

  const useremail = computed(() => data.value?.user?.email ?? 'N/A')
  const username = computed(() => data.value?.user?.name ?? 'N/A')

  /**
   * @description This function checks if current user is allowed to access specific path of the page.
   * @param {string} path - The path of the page that the user is trying to access.
   * @returns {boolean} - Returns true if the user has the specified roles.
   */
  function canAccessPage(path: string) {
    return can(roles.value, path)
  }

  /**
   * @note This is the app-side copy of `server/utils/doesUserHaveAnyRole.ts`
   * It was renamed due to a type configuration error on nitro
   * Once the problem is addressed, we can rename this function to `doesUserHaveAnyRole`
   * @see {@link https://github.com/sidestream-tech/hanselmann-os/pull/4704#issuecomment-2824887157}
   *
   * @description This function checks if current user has any of the roles in the given array of roles.
   * @param {readonly UserRole[]} rolesToCheck - The roles to check against the user's roles.
   * @param {boolean} [isAdminEveryRole] - If true, the function will return true if the user has the 'admin' role, regardless of other roles. Defaults to true.
   * @returns {boolean} - Returns true if the user has the specified roles or admin role, false otherwise.
   */
  function hasAnyAssignedRole(rolesToCheck: readonly UserRole[] = ['admin'], isAdminEveryRole = true) {
    return computed(() => hasAnyRole(roles.value, rolesToCheck, isAdminEveryRole))
  }

  /**
   * @note The function can be renamed when `hasAnyAssignedRole` is renamed to `doesUserHaveAnyRole`
   *
   * @description This function checks if current user has exact roles from the allowed list (subset), and no other roles.
   * @param {readonly UserRole[]} allowedRoles - The allowed roles.
   * @returns {boolean} - True if user has exact roles from allowedRoles (can be a subset), false otherwise.
   */
  function hasExactAssignedRoles(allowedRoles: readonly UserRole[]) {
    return computed(() => hasExactRoles(roles.value, allowedRoles))
  }

  return { canAccessPage, hasAnyAssignedRole, hasExactAssignedRoles, roles, username, useremail }
}
